/* eslint-disable @typescript-eslint/naming-convention */
import {useCallback} from 'react';
import {EventCurrencyCodes, EventNames} from 'modules/google-analytics/constants';
import {IProduct} from 'modules/product-card/models/ProductResponse';
import {getProductEcommerceData} from 'modules/product-card/analytics/gtag/helpers';
import {IWishlistProductShort, TAddToWishListResponse} from 'modules/wishlist_new/models';
import {sendGtagEvent} from 'hooks/analytics/gtag/helpers';

const WISHLIST_LIST_NAME = 'wishlist';

const buildWishlistEventLabel = (data?: TAddToWishListResponse['data']) => {
    if (!data) {
        return undefined;
    }

    const leftPart = data.wishlistId;
    const rightPart = data.article;

    if (!leftPart && !rightPart) {
        return undefined;
    }

    return [leftPart, rightPart].filter(Boolean).join('_');
};

// eslint-disable-next-line max-lines-per-function
export const useWishlistEvents = () => {
    const buildWishlistProduct = useCallback((product?: Partial<IWishlistProductShort> | null) => {
        if (!product) {
            return undefined;
        }

        return {
            ...getProductEcommerceData(product, 1),
            list: WISHLIST_LIST_NAME,
        };
    }, []);

    const sendAddToWishlistEvent = useCallback(
        (product?: IProduct | null, responseData?: TAddToWishListResponse['data']) => {
            if (!product) {
                return;
            }

            const wishlistProduct = buildWishlistProduct(product as unknown as IWishlistProductShort);
            if (!wishlistProduct) {
                return;
            }

            const dataLayerList = {
                event: EventNames.OWOX,
                eventCategory: WISHLIST_LIST_NAME,
                eventAction: 'addItemToWishlist',
                eventLabel: buildWishlistEventLabel(responseData),
                ecommerce: {
                    currencyCode: EventCurrencyCodes.RUB,
                    add_to_wishlist: {
                        actionField: {
                            list: WISHLIST_LIST_NAME,
                        },
                        products: [wishlistProduct],
                    },
                },
            };

            sendGtagEvent(dataLayerList);
        },
        [buildWishlistProduct],
    );

    const sendViewOwnWishlistEvent = useCallback((wishlistId?: number | string) => {
        if (!wishlistId) {
            return;
        }

        const dataLayerList = {
            event: EventNames.OWOX,
            eventCategory: WISHLIST_LIST_NAME,
            eventAction: 'view_own',
            eventLabel: String(wishlistId),
        };

        sendGtagEvent(dataLayerList);
    }, []);

    const sendShareWishlistEvent = useCallback((wishlistId?: number | string) => {
        if (!wishlistId) {
            return;
        }

        const dataLayerList = {
            event: EventNames.OWOX,
            eventCategory: WISHLIST_LIST_NAME,
            eventAction: 'share',
            eventLabel: String(wishlistId),
        };

        sendGtagEvent(dataLayerList);
    }, []);

    const sendViewGuestWishlistEvent = useCallback((wishlistId?: number | string) => {
        if (!wishlistId) {
            return;
        }

        const dataLayerList = {
            event: EventNames.OWOX,
            eventCategory: WISHLIST_LIST_NAME,
            eventAction: 'view_guest',
            eventLabel: String(wishlistId),
        };

        sendGtagEvent(dataLayerList);
    }, []);

    const sendReserveGiftEvent = useCallback((wishlistId?: number | string) => {
        if (!wishlistId) {
            return;
        }

        const dataLayerList = {
            event: EventNames.OWOX,
            eventCategory: WISHLIST_LIST_NAME,
            eventAction: 'checkWillGive',
            eventLabel: String(wishlistId),
        };

        sendGtagEvent(dataLayerList);
    }, []);

    const sendCancelReserveGiftEvent = useCallback((wishlistId?: number | string) => {
        if (!wishlistId) {
            return;
        }

        const dataLayerList = {
            event: EventNames.OWOX,
            eventCategory: WISHLIST_LIST_NAME,
            eventAction: 'uncheckWillGive',
            eventLabel: String(wishlistId),
        };

        sendGtagEvent(dataLayerList);
    }, []);

    const sendGoToPdpFromWishlistEvent = useCallback(
        (product?: Partial<IWishlistProductShort> | null, wishlistId?: number | string) => {
            if (!product || !wishlistId) {
                return;
            }

            const wishlistProduct = buildWishlistProduct(product);
            if (!wishlistProduct) {
                return;
            }

            const dataLayerList = {
                event: EventNames.OWOX,
                eventCategory: WISHLIST_LIST_NAME,
                eventAction: 'goToPDP',
                eventLabel: `${wishlistId}_${product.id ?? ''}`.replace(/_$/, ''),
                ecommerce: {
                    currencyCode: EventCurrencyCodes.RUB,
                    click: {
                        actionField: {
                            list: WISHLIST_LIST_NAME,
                        },
                        products: [wishlistProduct],
                    },
                },
            };

            sendGtagEvent(dataLayerList);
        },
        [buildWishlistProduct],
    );

    const sendRemoveWishlistItemEvent = useCallback(
        (product?: Partial<IWishlistProductShort> | null, wishlistId?: number | string) => {
            if (!product || !wishlistId) {
                return;
            }

            const wishlistProduct = buildWishlistProduct(product);
            if (!wishlistProduct) {
                return;
            }

            const dataLayerList = {
                event: EventNames.OWOX,
                eventCategory: WISHLIST_LIST_NAME,
                eventAction: 'removeItem',
                eventLabel: `${wishlistId}_${product.id ?? ''}`.replace(/_$/, ''),
                ecommerce: {
                    currencyCode: EventCurrencyCodes.RUB,
                    remove_from_wishlist: {
                        actionField: {
                            list: WISHLIST_LIST_NAME,
                        },
                        products: [wishlistProduct],
                    },
                },
            };

            sendGtagEvent(dataLayerList);
        },
        [buildWishlistProduct],
    );

    const sendRemoveAllWishlistEvent = useCallback(
        (products?: Partial<IWishlistProductShort>[] | null, wishlistId?: number | string) => {
            if (!wishlistId || !products || 0 === products.length) {
                return;
            }

            const wishlistProducts = products
                .map((product) => buildWishlistProduct(product))
                .filter(Boolean) as ReturnType<typeof buildWishlistProduct>[];

            if (0 === wishlistProducts.length) {
                return;
            }

            const dataLayerList = {
                event: EventNames.OWOX,
                eventCategory: WISHLIST_LIST_NAME,
                eventAction: 'removeAll',
                eventLabel: `${wishlistId}`,
                ecommerce: {
                    currencyCode: EventCurrencyCodes.RUB,
                    remove_from_wishlist: {
                        actionField: {
                            list: WISHLIST_LIST_NAME,
                        },
                        products: wishlistProducts,
                    },
                },
            };

            sendGtagEvent(dataLayerList);
        },
        [buildWishlistProduct],
    );

    return {
        sendAddToWishlistEvent,
        sendViewOwnWishlistEvent,
        sendShareWishlistEvent,
        sendViewGuestWishlistEvent,
        sendGoToPdpFromWishlistEvent,
        sendRemoveWishlistItemEvent,
        sendRemoveAllWishlistEvent,
        sendReserveGiftEvent,
        sendCancelReserveGiftEvent,
    };
};
